import { useEffect, useState } from 'react';

import type { DocumentTemplate, TemplateIssue } from '@/entities/templates';

import {
  IDLE_TEMPLATE_EDIT,
  type DroppedTemplateChanges,
  type TemplateDocumentFocus,
} from '../config';
import {
  finishTemplateEdit,
  followTemplateEditSource,
  holdsTemplateEditChanges,
  listTemplateDocumentChanges,
  replaceTemplateEditDraft,
  restartTemplateEdit,
  startTemplateEdit,
  type TemplateChangeSummary,
  type TemplateEditState,
} from '../lib';

type UpdateTemplateEdit = (
  recipe: (state: TemplateEditState) => TemplateEditState,
) => void;

type UpdateDroppedChanges = (
  recipe: (dropped: DroppedTemplateChanges) => DroppedTemplateChanges,
) => void;

const withoutSeries = (dropped: DroppedTemplateChanges, seriesId: string) =>
  Object.fromEntries(
    Object.entries(dropped).filter(([series]) => series !== seriesId),
  );

// The controls every template edit has over its state, wherever the state
// is kept: one edit per surface, or one per template series of a screen.
// `listChanges` names what a restart drops.
export const toTemplateEditControls = ({
  state,
  update,
  source,
  dropped,
  updateDropped,
  listChanges,
}: {
  state: TemplateEditState;
  update: UpdateTemplateEdit;
  // The version the edit starts from.
  source: DocumentTemplate | undefined;
  dropped: DroppedTemplateChanges;
  updateDropped: UpdateDroppedChanges;
  listChanges: (state: TemplateEditState) => TemplateChangeSummary[];
}) => {
  const base = state.source ?? source;
  const seriesId = base?.series_id ?? '';

  return {
    source: base,
    editing: state.editing,
    draft: state.draft,
    changed: holdsTemplateEditChanges(state),
    issues: state.issues,
    // Changes a reload of the series' current version dropped, kept in sight
    // until hidden, so the clinician can make them again by hand.
    droppedChanges: dropped[seriesId]?.changes ?? [],
    savedSinceDropped: !!dropped[seriesId]?.saved,
    start: () => {
      if (source) {
        update((current) => startTemplateEdit(current, source));
      }
    },
    // "Done": the edit mode closes; unsaved changes stay for the save bar.
    finish: () => update((current) => finishTemplateEdit(current, source)),
    cancel: () => update(() => IDLE_TEMPLATE_EDIT),
    // The changes are a saved version: the edit ends, and the changes a
    // reload dropped before stay listed against that version.
    complete: () => {
      update(() => IDLE_TEMPLATE_EDIT);
      updateDropped((currentDropped) =>
        currentDropped[seriesId]
          ? {
              ...currentDropped,
              [seriesId]: { ...currentDropped[seriesId], saved: true },
            }
          : currentDropped,
      );
    },
    // The edit starts again on the series' current version after a save was
    // refused because another version replaced the one it started from; its
    // changes are listed instead of made again.
    restart: (current: DocumentTemplate) => {
      // The list is read in the edit's own update, from the draft it drops,
      // which holds the changes made while the current version was read too.
      // Updates apply in the order they are made, so the list is read before
      // it is added.
      let changes: TemplateChangeSummary[] = [];
      update((latest) => {
        changes = listChanges(latest);
        return restartTemplateEdit(latest, current);
      });
      updateDropped((currentDropped) => ({
        ...currentDropped,
        [current.series_id]: {
          changes: [
            ...(currentDropped[current.series_id]?.changes ?? []),
            ...changes,
          ],
          saved: false,
        },
      }));
    },
    hideDroppedChanges: () =>
      updateDropped((currentDropped) =>
        withoutSeries(currentDropped, seriesId),
      ),
    reportIssues: (issues: TemplateIssue[]) =>
      update((current) => ({ ...current, issues })),
  };
};

export type TemplateEditControls = ReturnType<typeof toTemplateEditControls>;

const listDocumentChanges = ({ source, draft }: TemplateEditState) =>
  source && draft ? listTemplateDocumentChanges(source, draft.template) : [];

// A block template edited on the Templates page as one document, saved
// later as a clinic version. The document keeps the edit's undo and redo
// and writes the whole draft back on every change.
export const useTemplateEdit = ({
  source,
}: {
  source: DocumentTemplate | undefined;
}) => {
  const [state, setState] = useState<TemplateEditState>(IDLE_TEMPLATE_EDIT);
  const [dropped, setDropped] = useState<DroppedTemplateChanges>({});
  const [focus, setFocus] = useState<TemplateDocumentFocus>();
  const sourceId = source?.id;

  useEffect(() => {
    setState((current) => followTemplateEditSource(current, source));
    // The version object follows its id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId]);

  return {
    ...toTemplateEditControls({
      state,
      update: setState,
      source,
      dropped,
      updateDropped: setDropped,
      listChanges: listDocumentChanges,
    }),
    // What the document cannot save as it is.
    documentIssues: state.documentIssues,
    focus,
    // The document opens at a line, else a section.
    show: (targetId: string) => {
      if (source) {
        setState((current) => startTemplateEdit(current, source));
      }
      setFocus((current) => ({ targetId, at: (current?.at ?? 0) + 1 }));
    },
    change: ({
      template,
      issues,
    }: {
      template: DocumentTemplate;
      issues: TemplateIssue[];
    }) =>
      setState((current) =>
        current.draft
          ? {
              ...replaceTemplateEditDraft(current, template),
              documentIssues: issues,
            }
          : current,
      ),
  };
};

export type TemplateEdit = ReturnType<typeof useTemplateEdit>;
